'use client';
import Link from 'next/link';
import { useAuthStore } from '@/stores/authStore';
import { useQuery } from '@tanstack/react-query';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { buttonVariants } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  Users, FileText, Building2, Bell,
  Clock, AlertTriangle, CheckCircle2, FolderOpen,
  TrendingUp, Banknote, Percent, Plus,
} from 'lucide-react';

// ── Tipi locali ───────────────────────────────────────────────────────────────

interface ScadenzaItem {
  id: string;
  codice_pratica: string;
  fase: string;
  cliente: string;
  prima_richiesta: string | null;
}

interface BrokerStats {
  contatori: {
    attive: number;
    inAttesaDocumenti: number;
    inIstruttoria: number;
    conclUseMese: number;
  };
  scadenzeImminenti: ScadenzaItem[];
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function orePassate(dataISO: string): number {
  return Math.floor((Date.now() - new Date(dataISO).getTime()) / (1000 * 60 * 60));
}

// ── Pagina ────────────────────────────────────────────────────────────────────

export default function DashboardPage() {
  const utente      = useAuthStore((s) => s.utente);
  const isSuperadmin = utente?.ruolo === 'superadmin';

  // Superadmin
  const { data: adminStats } = useQuery({
    queryKey: ['admin-stats'],
    queryFn: () => api.get('/admin/stats').then((r) => r.data),
    enabled: isSuperadmin,
  });

  // Broker / Operatore
  const { data: brokerStats } = useQuery<{ data: BrokerStats }>({
    queryKey: ['broker-stats'],
    queryFn: () => api.get('/pratiche/stats'),
    enabled: !isSuperadmin,
    refetchInterval: 60_000,
  });

  const { data: notifiche } = useQuery({
    queryKey: ['notifiche'],
    queryFn: () => api.get('/notifiche?limit=8').then((r) => r.data),
    enabled: !isSuperadmin,
  });

  const { data: pratiche } = useQuery({
    queryKey: ['pratiche-recenti'],
    queryFn: () => api.get('/pratiche?limit=6').then((r) => r.data),
    enabled: !isSuperadmin,
  });

  // ── Superadmin view ────────────────────────────────────────────────────────
  if (isSuperadmin) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold">Benvenuto, {utente?.nome}</h1>
          <p className="text-muted-foreground">Pannello amministrazione globale</p>
        </div>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <StatCard title="Tenant" value={adminStats?.totali?.tenants ?? '—'} icon={Building2} color="blue" />
          <StatCard title="Utenti" value={adminStats?.totali?.utenti ?? '—'} icon={Users} color="green" />
          <StatCard title="Clienti" value={adminStats?.totali?.clienti ?? '—'} icon={Users} color="purple" />
          <StatCard title="Pratiche" value={adminStats?.totali?.pratiche ?? '—'} icon={FileText} color="orange" />
        </div>
        {adminStats?.pratichePerStato?.length > 0 && (
          <Card>
            <CardHeader><CardTitle>Pratiche per stato</CardTitle></CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {adminStats.pratichePerStato.map((p: { stato: string; count: number }) => (
                  <Badge key={p.stato} variant="secondary" className="text-sm py-1 px-3">
                    {p.stato.replace(/_/g, ' ')}: <span className="font-bold ml-1">{p.count}</span>
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    );
  }

  // ── Broker / Operatore view ────────────────────────────────────────────────
  const cnt = brokerStats?.data?.contatori;
  const scadenze = brokerStats?.data?.scadenzeImminenti ?? [];

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">Benvenuto, {utente?.nome}!</h1>
          <p className="text-muted-foreground">Riepilogo aggiornato in tempo reale</p>
        </div>
        <Link href="/dashboard/pratiche?nuova=1" className={`${buttonVariants()} shrink-0`}>
          <Plus className="size-4 mr-2" /> Nuova Pratica
        </Link>
      </div>

      {/* Contatori principali */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard title="Pratiche attive"          value={cnt?.attive            ?? '—'} icon={FolderOpen}   color="blue" />
        <StatCard title="In attesa documenti"       value={cnt?.inAttesaDocumenti ?? '—'} icon={Clock}        color="orange" />
        <StatCard title="In istruttoria"            value={cnt?.inIstruttoria     ?? '—'} icon={FileText}     color="purple" />
        <StatCard title="Concluse questo mese"      value={cnt?.conclUseMese      ?? '—'} icon={CheckCircle2} color="green" />
      </div>

      {/* KPI mensili */}
      <KpiMese />

      {/* Scadenze imminenti */}
      {scadenze.length > 0 && (
        <Card className="border-orange-200">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-orange-700">
              <AlertTriangle className="size-4" />
              Pratiche con documenti in attesa da oltre 48h
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Pratica</TableHead>
                  <TableHead>Cliente</TableHead>
                  <TableHead>Stato</TableHead>
                  <TableHead>Richiesta inviata</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {scadenze.map((p) => (
                  <TableRow key={p.id}>
                    <TableCell>
                      <Link href={`/dashboard/pratiche/${p.id}`} className="font-mono text-sm font-medium hover:underline">
                        {p.codice_pratica}
                      </Link>
                    </TableCell>
                    <TableCell className="text-sm">{p.cliente}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-xs">
                        {p.fase}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {p.prima_richiesta
                        ? `${orePassate(p.prima_richiesta)}h fa`
                        : '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Riga inferiore: pratiche recenti + notifiche */}
      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-3">
            <CardTitle className="text-sm font-medium">Pratiche recenti</CardTitle>
            <Link href="/dashboard/pratiche" className="text-xs text-muted-foreground hover:underline">
              Vedi tutte →
            </Link>
          </CardHeader>
          <CardContent className="p-0">
            {!pratiche?.items?.length && (
              <p className="text-sm text-muted-foreground px-6 pb-4">Nessuna pratica</p>
            )}
            <ul>
              {pratiche?.items?.slice(0, 6).map((p: { id: string; codice_pratica: string; fase_corrente_nome?: string; cliente?: { ragione_sociale: string } }) => (
                <li key={p.id} className="flex items-center justify-between px-6 py-2.5 border-b last:border-0 hover:bg-muted/30 transition-colors">
                  <div className="flex flex-col min-w-0">
                    <Link href={`/dashboard/pratiche/${p.id}`} className="font-mono text-sm font-medium hover:underline truncate">
                      {p.codice_pratica}
                    </Link>
                    {p.cliente && (
                      <span className="text-xs text-muted-foreground truncate">{p.cliente.ragione_sociale}</span>
                    )}
                  </div>
                  <Badge variant="outline" className="text-xs shrink-0 ml-2">
                    {p.fase_corrente_nome ?? '—'}
                  </Badge>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Bell className="size-4" />
              Notifiche recenti
            </CardTitle>
            <Link href="/dashboard/notifiche" className="text-xs text-muted-foreground hover:underline">
              Vedi tutte →
            </Link>
          </CardHeader>
          <CardContent className="p-0">
            {!notifiche?.items?.length && (
              <p className="text-sm text-muted-foreground px-6 pb-4">Nessuna notifica</p>
            )}
            <ul>
              {notifiche?.items?.slice(0, 8).map((n: { id: string; titolo: string; messaggio?: string; letta: boolean; created_at: string }) => (
                <li key={n.id} className="flex items-start gap-3 px-6 py-2.5 border-b last:border-0">
                  {!n.letta && <span className="size-2 rounded-full bg-blue-500 shrink-0 mt-1.5" />}
                  <div className={`flex-1 min-w-0 ${n.letta ? 'opacity-60' : ''}`}>
                    <p className="text-sm font-medium truncate">{n.titolo}</p>
                    <p className="text-xs text-muted-foreground">
                      {new Date(n.created_at).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// ── Componente StatCard ───────────────────────────────────────────────────────

function StatCard({ title, value, icon: Icon, color }: {
  title: string; value: number | string;
  icon: React.ElementType; color: string;
}) {
  const colors: Record<string, string> = {
    blue:   'bg-blue-100 text-blue-600',
    green:  'bg-green-100 text-green-600',
    purple: 'bg-purple-100 text-purple-600',
    orange: 'bg-orange-100 text-orange-600',
  };
  return (
    <Card>
      <CardContent className="flex items-center gap-4 p-6">
        <div className={`p-3 rounded-lg shrink-0 ${colors[color]}`}>
          <Icon className="size-5" />
        </div>
        <div>
          <p className="text-sm text-muted-foreground">{title}</p>
          <p className="text-2xl font-bold">{value}</p>
        </div>
      </CardContent>
    </Card>
  );
}

// ── KPI Mese ─────────────────────────────────────────────────────────────────

interface KpiData {
  data: {
    mese: string;
    pratiche_aperte: number;
    pratiche_erogate: number;
    tasso_completamento_perc: number;
    ricavi: number;
    costi: { broker_chain: number; ore_consulenti: number; api: number; totale: number };
    margine: number;
    serie6mesi: { mese: string; aperte: number; erogate: number }[];
  };
}

function KpiMese() {
  const { data } = useQuery<KpiData>({
    queryKey: ['kpi-mese'],
    queryFn:  () => api.get('/pratiche/kpi-mese').then((r) => r.data),
    refetchInterval: 60_000,
  });

  if (!data) return null;
  const k = data.data;
  const eur = (n: number) => `€ ${n.toFixed(2).replace('.', ',')}`;
  const margPos = k.margine >= 0;

  const maxAperte = Math.max(1, ...k.serie6mesi.map((s) => s.aperte));
  const maxEro    = Math.max(1, ...k.serie6mesi.map((s) => s.erogate));
  const maxSerie  = Math.max(maxAperte, maxEro);

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <TrendingUp className="size-5 text-blue-600" /> KPI mese in corso
          <span className="text-xs font-normal text-muted-foreground ml-2">
            {new Date(k.mese + '-01').toLocaleDateString('it-IT', { month: 'long', year: 'numeric' })}
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <KpiCard label="Tasso completamento documentale" value={`${k.tasso_completamento_perc.toFixed(1)}%`} sub={`${k.pratiche_aperte} pratiche aperte`} icon={Percent} />
          <KpiCard label="Pratiche erogate"   value={String(k.pratiche_erogate)} sub="nel mese" icon={CheckCircle2} />
          <KpiCard label="Ricavi maturati"    value={eur(k.ricavi)} sub={`Costi: ${eur(k.costi.totale)}`} icon={Banknote} positive />
          <KpiCard
            label="Margine"
            value={eur(k.margine)}
            sub={`Broker ${eur(k.costi.broker_chain)} · Ore ${eur(k.costi.ore_consulenti)} · API ${eur(k.costi.api)}`}
            icon={TrendingUp}
            color={margPos ? 'green' : 'red'}
          />
        </div>

        {/* Serie 6 mesi */}
        <div className="mt-6">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-3">
            Ultimi 6 mesi — aperte vs erogate
          </p>
          <div className="flex items-end gap-2 h-32">
            {k.serie6mesi.map((s) => (
              <div key={s.mese} className="flex-1 flex flex-col items-center gap-1">
                <div className="flex items-end gap-0.5 flex-1 w-full">
                  <div
                    className="bg-blue-500 rounded-t flex-1"
                    style={{ height: `${(s.aperte / maxSerie) * 100}%`, minHeight: s.aperte > 0 ? 2 : 0 }}
                    title={`${s.aperte} aperte`}
                  />
                  <div
                    className="bg-green-500 rounded-t flex-1"
                    style={{ height: `${(s.erogate / maxSerie) * 100}%`, minHeight: s.erogate > 0 ? 2 : 0 }}
                    title={`${s.erogate} erogate`}
                  />
                </div>
                <span className="text-[10px] text-muted-foreground font-mono">
                  {s.mese.slice(5)}
                </span>
              </div>
            ))}
          </div>
          <div className="flex gap-4 mt-2 text-xs text-muted-foreground">
            <span className="flex items-center gap-1"><span className="size-3 rounded bg-blue-500" /> Aperte</span>
            <span className="flex items-center gap-1"><span className="size-3 rounded bg-green-500" /> Erogate</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function KpiCard({ label, value, sub, icon: Icon, color, positive }: {
  label: string; value: string; sub?: string; icon: React.ElementType;
  color?: 'green' | 'red'; positive?: boolean;
}) {
  const cls = color === 'red'
    ? 'text-destructive'
    : (color === 'green' || positive)
      ? 'text-green-600'
      : 'text-foreground';
  return (
    <div className="p-4 rounded-lg border bg-card">
      <div className="flex items-center gap-2 text-xs text-muted-foreground mb-2">
        <Icon className="size-3.5" /> {label}
      </div>
      <p className={`text-xl font-bold tabular-nums ${cls}`}>{value}</p>
      {sub && <p className="text-xs text-muted-foreground mt-1 truncate" title={sub}>{sub}</p>}
    </div>
  );
}
